// @ts-check

// Vendor imports
const _ = require('lodash')
const Bowser = require('bowser')
// Sibling imports
const branchIntegration = require('./branch')
const globalPageContext = require('./page-context')
const protobufCommands = require('./protobuf-commands')
const segmentIntegration = require('./segment')

/**
 * This module is only intended for use in a client-side web-browser execution context;
 * however it is incidentally required on Node during testing and server-side rendering.
 * This conditional is to prevent `require('client/lib/business-events')` from failing;
 * `getAppContext` should never be called outside of the browser and should fail if it ever is.
 * @type {Bowser.Parser.Parser}
 */
let browser
if (typeof navigator !== 'undefined') {
  try {
    browser = Bowser.getParser(navigator.userAgent)
  } catch (err) {
    /**
     * Swallow user-agent parse errors
     * @see https://app.asana.com/0/102902769366319/1166044811950463
     */
  }
}

/** @typedef { import('./types.d').DenormalizedPageContext } DenormalizedPageContext */
/** @typedef { import('./types.d').PageContext } PageContext */
/** @typedef { import('./types.d').PageContextWithReferrer } PageContextWithReferrer */
/** @typedef { import('./types.d').ClientSession } ClientSession */
/** @typedef { import('./types.d').UtmParams } UtmParams */
/** @typedef { import('./types.d').User } User */

/** @typedef {Object.<string, string>} UserExperiments */
/** @typedef {Object.<string, boolean>} UserRollouts */

/** @typedef {function(PageContextWithReferrer): string} GetPageNameCallback */
/** @typedef {function(): string} GetSessionIdCallback */

/** @typedef { keyof typeof import('messages/client/protobuf.d').commands } ProtobufCommandName */

// Configuration for client v1-v2 cross-compatibility
const NOT_CONFIGURED = () => { throw new Error('client/lib/business-events is not configured!') }
/** @type {boolean} */
let isBot
/** @type {boolean} */
let isLoggedIn
/** @type {string} */
let buildNumber
/** @type {UserExperiments} */
let experiments = {}
/** @type {UserRollouts} */
let rollouts = {}
/** @type {GetPageNameCallback} */
let getPageName = NOT_CONFIGURED
/** @type {GetSessionIdCallback} */
let getSessionId = NOT_CONFIGURED

// Attempt to auto-configure for v1 (wiggin) client
if (typeof wiggin !== 'undefined') {
  configure({
    // @ts-ignore global wiggin types are not defined
    user: wiggin.user,
    // @ts-ignore global wiggin types are not defined
    buildNumber: wiggin.buildNumber,
    getPageName: pageContext => pageContext['page_name'] || app.state('page'),
    getSessionId: () => app.sess.update()
  })
}

/**
 * @param {Object} options
 * @param {Object} [options.user]
 * @param {boolean} options.user.bot
 * @param {UserExperiments} options.user.experiments
 * @param {boolean} options.user.loggedIn
 * @param {UserRollouts} options.user.rollouts
 * @param {string} [options.buildNumber]
 * @param {GetPageNameCallback} [options.getPageName]
 * @param {GetSessionIdCallback} [options.getSessionId]
 */
function configure(options) {
  if (options.user) {
    isBot = options.user.bot
    isLoggedIn = options.user.loggedIn
    experiments = options.user.experiments
    rollouts = options.user.rollouts
  }
  if (options.buildNumber) {
    buildNumber = options.buildNumber
  }
  if (options.getPageName) {
    getPageName = options.getPageName
  }
  if (options.getSessionId) {
    getSessionId = options.getSessionId
  }
}

/**
 * @param {DenormalizedPageContext} pageContextOverlay
 * @returns {PageContextWithReferrer}
 */
function getEventPageContext(pageContextOverlay) {
  return _.merge(
    // Some browsers omit empty objects; __keep ensures the page context is always sent.
    { '__keep': true },
    globalPageContext.getPageContext(),
    globalPageContext.normalizePageContext(pageContextOverlay)
  )
}

/**
 * @param {User} user
 * @returns {Promise.<void>}
 */
async function identifyUser(user) {
  // eslint-disable-next-line no-console
  console.log('--- analytics.identify ---')
  await segmentIntegration.identifyUser(user)
}

/**
 * @param {ProtobufCommandName} commandName
 * @param {any} params
 * @param {Object} [options]
 * @param {string} [options.commandUrl]
 * @param {number} [options.eventTimestamp]
 * @param {DenormalizedPageContext} [options.pageContext]
 * @param {string} [options.sessionId]
 * @returns {Promise.<void>}
 */
async function trackProtobuf(commandName, params, options = {}) {
  // We do not track events for bots or logged out users.
  if (isBot || !isLoggedIn) {
    return
  }
  // Capture the time of the event, then wait for commands to load
  const eventTimestamp = options.eventTimestamp || Date.now()
  await protobufCommands.sendCommand(commandName, params, {
    browser,
    buildNumber,
    commandUrl: options.commandUrl,
    eventTimestamp,
    experiments,
    rollouts,
    pageContext: getEventPageContext(options.pageContext),
    sessionId: options.sessionId || getSessionId()
  })
}

/**
 * Send a tracking call using the Segment SDK
 * @param {string} eventName
 * @param {Object<string, any>} [properties]
 * @see https://segment.com/docs/spec/track/#properties
 * @param {Object} [options]
 * @param {DenormalizedPageContext} [options.pageContext]
 * @returns {Promise.<void>}
 */
async function trackSegment(eventName, properties = {}, options = {}) {
  // We do not track Segment events for bot users
  if (isBot) {
    return
  }
  const pageContext = getEventPageContext(options.pageContext)
  const branchParams = branchIntegration.getBusinessEventParams()
  // Convert experiments Object into a comma-separated key=value string
  const testGroups = Object.entries(experiments).map(pair => pair.join('=')).join(',')
  /* eslint-disable no-console */
  console.log('--- analytics.track ---')
  console.log({
    eventName,
    ...properties,
    'branch_params': branchParams,
    page: getPageName(pageContext),
    rollouts: Object.keys(rollouts),
    'test_groups': testGroups
  })
  /* eslint-enable no-console */
  await segmentIntegration.track(eventName, {
    ...properties,
    'branch_params': branchParams,
    page: getPageName(pageContext),
    rollouts: Object.keys(rollouts),
    'test_groups': testGroups
  })
  .catch(() => { /* Ignore failed Segment calls */ })
}

module.exports = {
  configure,
  getEventPageContext,
  identifyUser,
  trackProtobuf,
  trackSegment
}
